import {
  flattenPlaybookTreeWithDepth,
  getPlaybookNode,
  isPlaybookSectionNode,
  type PlaybookNode,
  type PlaybookNodeId,
  type PlaybookTree,
} from "@repo/app-types/entities/playbook";
import type {
  FeedbackWithCurrentGrade,
  RecordingAssessmentFeedbackStatus,
} from "@repo/app-types/entities/recordingAssessmentFeedback";
import { Size } from "@repo/ui/helpers/helpers";
import {
  SegmentedControl,
  type SegmentedControlOption,
} from "@repo/ui/toggleButton/segmentedControl";
import { type FC, useMemo } from "react";
import { FeedbackReviewNavigation } from "./feedbackReviewNavigation.js";
import { FeedbackReviewNodePanel } from "./feedbackReviewNodePanel.js";
import { FeedbackReviewTable } from "./feedbackReviewTable.js";
import { getPlaybookOpenFeedbackPosition } from "./feedbackReviewTreeHelpers.js";

const feedbackStatusOptions: readonly SegmentedControlOption<RecordingAssessmentFeedbackStatus>[] =
  [
    { id: "open", label: "Open" },
    { id: "resolved", label: "Resolved" },
    { id: "outdated", label: "Outdated" },
  ];

export interface PlaybookFeedbackReviewTabProps {
  /** The playbook's feedback in `status`, in server order. */
  feedback: readonly FeedbackWithCurrentGrade[];
  /** True while the feedback for `status` is still loading. */
  isFeedbackLoading: boolean;
  /** True when the feedback for `status` failed to load. */
  isFeedbackError: boolean;
  /** Open feedback per node: decides where the tab lands and drives navigation. */
  openFeedbackCounts: ReadonlyMap<PlaybookNodeId, number>;
  /** Draft tree that determines available review targets and their order. */
  draftTree: PlaybookTree;
  /** Published tree whose node content was used to grade the feedback. */
  liveTree: PlaybookTree;
  /** The URL-resolved review target. */
  pickedNodeId: PlaybookNodeId | null;
  onPickNode: (nodeId: PlaybookNodeId) => void;
  status: RecordingAssessmentFeedbackStatus;
  onStatusChange: (status: RecordingAssessmentFeedbackStatus) => void;
  /** Edit-route URL for the currently selected node. */
  editHref: string;
  /** Opens the assistant's edit-suggestion flow for the selected node. */
  onSuggestEdits?: (node: PlaybookNode) => void;
  /** True while the assistant is already handling a request. */
}

/**
 * The playbook editor's "Review feedback" tab: one criterion (or section) at a
 * time, its grading instructions on the left and the feedback reviewers left on
 * it on the right.
 */
export const PlaybookFeedbackReviewTab: FC<PlaybookFeedbackReviewTabProps> = (
  props,
) => {
  const {
    feedback,
    isFeedbackLoading,
    isFeedbackError,
    openFeedbackCounts,
    draftTree,
    liveTree,
    pickedNodeId,
    onPickNode,
    status,
    onStatusChange,
    editHref,
    onSuggestEdits,
  } = props;

  const flattenedNodes = useMemo(
    () => flattenPlaybookTreeWithDepth(draftTree),
    [draftTree],
  );

  const selectedEntry = flattenedNodes.find(
    (entry) => entry.node.id === pickedNodeId,
  );
  const selectedNodeId = selectedEntry?.node.id ?? null;
  const liveNode = selectedNodeId
    ? getPlaybookNode(selectedNodeId, liveTree)
    : undefined;

  const feedbackItems = useMemo(
    () => feedback.filter((item) => item.criteria_node_id === selectedNodeId),
    [feedback, selectedNodeId],
  );

  return (
    <div className="grid h-full min-h-0 grid-cols-[minmax(300px,380px)_minmax(0,1fr)] gap-4 p-4">
      <FeedbackReviewNodePanel
        draftNode={selectedEntry?.node}
        node={liveNode}
        openFeedbackPosition={getPlaybookOpenFeedbackPosition(
          flattenedNodes,
          openFeedbackCounts,
          selectedNodeId,
        )}
      />

      <div className="flex min-h-0 flex-col gap-3">
        <div className="flex shrink-0 items-center gap-3">
          <SegmentedControl
            aria-label="Feedback status"
            size={Size.SMALL}
            options={feedbackStatusOptions}
            value={status}
            onChange={onStatusChange}
          />
          <div className="flex-1" />
          <FeedbackReviewNavigation
            flattenedNodes={flattenedNodes}
            openFeedbackCounts={openFeedbackCounts}
            selectedNodeId={selectedNodeId}
            onSelectNode={onPickNode}
            editHref={editHref}
            onSuggestEdits={
              selectedEntry && onSuggestEdits
                ? () => onSuggestEdits(selectedEntry.node)
                : undefined
            }
          />
        </div>

        {/* The table owns its own scrolling, so bound its height here. */}
        <div className="flex min-h-0 flex-1 flex-col">
          <FeedbackReviewTable
            feedbackItems={feedbackItems}
            isLoading={isFeedbackLoading}
            isError={isFeedbackError}
            isSectionSelected={
              selectedEntry !== undefined &&
              isPlaybookSectionNode(selectedEntry.node)
            }
            status={status}
          />
        </div>
      </div>
    </div>
  );
};
